import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";
import { getToken } from "../components/auth/authConfig";

/**
 * Report chat, routed through the Enterprise Intelligence Engine.
 *
 * The portal's role is to capture what is on screen and render what comes back.
 * It does not build prompts, choose a model, or compose an answer — the engine
 * owns planning, retrieval, governance and evaluation. See
 * docs/enterprise-engine/01-TARGET-ARCHITECTURE.md.
 *
 * The engine can return three outcomes and the UI must handle all of them:
 *
 *   answered      → headline + narrative + evidence, assumptions, limitations
 *   clarification → up to two targeted questions; NOTHING was executed
 *   refused       → out of scope or not entitled, with a suggested alternative
 *
 * The legacy prompt-only endpoint is still reachable via `useLegacyPath` so the
 * ENGINE_ENABLED kill switch has a client-side counterpart during rollout.
 */

const EMPTY_RESULT = {
  status: null,
  answer: null,
  clarification: null,
  refusal: null,
  trace: null,
};

/** Normalise the engine's JSON into the shape the chat component renders. */
function normaliseEngineResponse(data) {
  if (!data) return EMPTY_RESULT;

  const answer = data.answer || null;
  return {
    status: data.status || null,
    answer: answer
      ? {
          headline: answer.headline || "",
          narrative: answer.narrative || "",
          keyPoints: answer.key_points || [],
          confidence: answer.confidence || null,
          citations: answer.citations || [],
          assumptions: answer.assumptions || [],
          limitations: answer.limitations || [],
          followUps: answer.follow_ups || [],
          table: answer.table || null,
          // Typed chart specs computed from evidence. Unlike the previous
          // implementation these are never scraped out of generated prose, so
          // the chart and the narrative always show the same numbers.
          charts: answer.charts || [],
          requiresHumanReview: answer.requires_human_review || false,
          reviewReason: answer.review_reason || null,
          machinePayload: answer.machine_payload || {},
        }
      : null,
    clarification: data.clarification || null,
    refusal: data.refusal || null,
    trace: data.trace || null,
  };
}

/** Render an engine response as the plain text a chat bubble shows. */
export function engineResponseToText(result) {
  if (!result) return "";
  if (result.answer) {
    return [result.answer.headline, result.answer.narrative]
      .filter(Boolean)
      .join("\n\n");
  }
  if (result.clarification) {
    const questions = (result.clarification.questions || [])
      .map((q, i) => {
        const options = (q.options || []).length
          ? `\n   Options: ${q.options.join(" · ")}`
          : "";
        return `${i + 1}. ${q.question}${options}`;
      })
      .join("\n");
    const reason = result.clarification.reason || "I need a little more detail:";
    return `${reason}\n\n${questions}`;
  }
  if (result.refusal) {
    return [result.refusal.reason, result.refusal.suggestion]
      .filter(Boolean)
      .join("\n\n");
  }
  return "Sorry, I couldn't get a response.";
}

export const useReportChatBot = ({ useLegacyPath = true } = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const askReportQuestion = useCallback(
    async ({
      question,
      reportName,
      workspaceName,
      pageName,
      visuals,
      filters,
      rlsRoles,
      conversationId,
      priorTurns,
    }) => {
      setLoading(true);
      setError(null);
      try {
        const url = useLegacyPath
          ? apiUrl.aipdChatReport()
          : apiUrl.aipdChatEngine();
        const response = await apiService.post(url, {
          question,
          reportName,
          workspaceName,
          pageName,
          visuals,
          filters,
          rlsRoles,
          conversationId,
          priorTurns,
        });
        return useLegacyPath
          ? { status: "answered", answer: { headline: response.data?.answer || "" } }
          : normaliseEngineResponse(response.data);
      } catch (err) {
        const message =
          err.response?.data?.detail ||
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message;
        setError(message);
        console.error("Error asking report question:", message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [useLegacyPath]
  );

  /**
   * Stream the same question over SSE.
   *
   * Engine event names: accepted · progress · stage · node · answer ·
   * clarification · refusal · trace · error · done.
   *
   * `onProgress` drives the live progress list and is the only one a UI should
   * render: it delivers one keyed, labelled row per step, updating in place.
   * `stage` and `node` remain the raw trace — three frames per stage, most
   * without a label — and are forwarded only for a debug view. Rendering them
   * is what produced duplicate steps and blank rows.
   */
  const streamReportQuestion = useCallback(
    async (
      {
        question,
        reportName,
        workspaceName,
        pageName,
        visuals,
        filters,
        rlsRoles,
        conversationId,
        priorTurns,
        refresh,
        sessionId,
        reportId,
        groupId,
      },
      handlers = {}
    ) => {
      const { onProgress, onStage, onNode, onAccepted } = handlers;
      setLoading(true);
      setError(null);

      try {
        const token = await getToken();
        const url = useLegacyPath
          ? apiUrl.aipdChatReportStream()
          : apiUrl.aipdChatEngineStream();

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "oidc-token": token,
          },
          body: JSON.stringify({
            question,
            reportName,
            workspaceName,
            pageName,
            visuals,
            filters,
            rlsRoles,
            conversationId,
            priorTurns,
            // Explicit "ask again": recompute instead of serving the cached
            // answer. Only ever set from a user action.
            refresh: refresh === true,
            // Resumes (or, once created, keeps writing to) a saved conversation
            // — omitted on the first turn, the backend creates one and reports
            // its id back via the "session" event below.
            sessionId,
            reportId,
            groupId,
          }),
        });

        if (!response.ok || !response.body) {
          throw new Error(`Stream request failed (${response.status})`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let result = { ...EMPTY_RESULT };
        // The legacy endpoint reports a stage as started only; it never says a
        // stage finished, so the previous row is closed when the next one opens.
        let openLegacyStep = null;

        const closeLegacyStep = () => {
          if (!openLegacyStep) return;
          onProgress?.({ ...openLegacyStep, status: "completed" });
          openLegacyStep = null;
        };

        /**
         * Translate the legacy `/chat/report/stream` contract (session · stage ·
         * batch · answer · graphs · error) into the engine-shaped progress rows
         * and answer envelope the UI already renders, so the kill switch does
         * not need a second rendering path.
         */
        const handleLegacyEvent = (eventName, data) => {
          switch (eventName) {
            case "stage": {
              closeLegacyStep();
              openLegacyStep = {
                key: `stage:${data.stage}`,
                label: data.message || data.stage || "Working",
              };
              onProgress?.({ ...openLegacyStep, status: "running" });
              break;
            }
            case "batch": {
              closeLegacyStep();
              onProgress?.({
                key: `batch:${data.index}`,
                label:
                  data.total > 1
                    ? `Analysed section ${data.index} of ${data.total}`
                    : "Analysed the report data",
                status: "completed",
              });
              break;
            }
            case "answer": {
              closeLegacyStep();
              result = {
                ...EMPTY_RESULT,
                ...result,
                status: "answered",
                answer: {
                  headline: "",
                  narrative: data.answer || "",
                  keyPoints: [],
                  confidence: null,
                  citations: [],
                  assumptions: [],
                  limitations: [],
                  followUps: [],
                  table: null,
                  charts: result.answer?.charts || [],
                  requiresHumanReview: false,
                  reviewReason: null,
                  machinePayload: {},
                },
              };
              break;
            }
            case "graphs": {
              const graph = data.graphData;
              if (!graph?.x_key || !(graph.y_keys || []).length) break;
              const chart = {
                chart_type: graph.type,
                title: graph.title || "",
                x_field: graph.x_key,
                y_fields: graph.y_keys,
                data: graph.data || [],
              };
              result = {
                ...result,
                answer: result.answer
                  ? { ...result.answer, charts: [...result.answer.charts, chart] }
                  : null,
              };
              break;
            }
            case "session":
              result = { ...result, sessionId: data.sessionId };
              break;
            case "error":
              throw new Error(data.message || "The request could not be completed.");
            default:
              break;
          }
        };

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let boundary;
          while ((boundary = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);

            // A heartbeat frame is a bare comment line and carries no data;
            // it exists only to keep proxies from closing a slow stream.
            if (!rawEvent.trim() || rawEvent.trimStart().startsWith(":")) continue;

            let eventName = "message";
            let dataLine = "";
            for (const line of rawEvent.split("\n")) {
              if (line.startsWith("event:")) eventName = line.slice(6).trim();
              else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
            }
            if (!dataLine) continue;

            let data;
            try {
              data = JSON.parse(dataLine);
            } catch {
              continue;
            }

            if (useLegacyPath) {
              handleLegacyEvent(eventName, data);
              continue;
            }

            switch (eventName) {
              case "accepted":
                onAccepted?.(data);
                break;
              case "progress":
                onProgress?.(data);
                break;
              case "stage":
                onStage?.(data);
                break;
              case "node":
                onNode?.(data);
                break;
              // `answer` is the single terminal event and it carries the whole
              // response envelope — `{status, answer, clarification, refusal,
              // trace}` — not just the answer object. Wrapping it again as
              // `{status: "answered", answer: data}` put the real payload one
              // level too deep, so `headline` resolved to undefined and EVERY
              // streamed reply rendered as an empty bubble regardless of
              // outcome. The envelope already states its own status, so pass it
              // straight through; this handles answered, clarification and
              // refused in one branch.
              case "answer":
                result = normaliseEngineResponse(data);
                break;
              case "trace":
                result = { ...result, trace: data };
                break;
              // The backend creates (or resolves) the saved-conversation row
              // after the answer is persisted and reports its id here — the
              // only place this endpoint's session id is ever learned.
              case "session":
                result = { ...result, sessionId: data.sessionId };
                break;
              case "error":
                throw new Error(data.message || "The engine could not complete the request.");
              default:
                break;
            }
          }
        }

        closeLegacyStep();
        return result;
      } catch (err) {
        setError(err.message);
        console.error("Error streaming report question:", err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [useLegacyPath]
  );

  // Conversation history — list/create sessions, fetch a past session's messages
  const listChatSessions = useCallback(async ({ reportId, groupId }) => {
    try {
      const response = await apiService.get(apiUrl.aipdChatSessions(reportId, groupId));
      return response.data?.sessions || [];
    } catch (err) {
      console.error("Error listing chat sessions:", err.response?.data?.message || err.message);
      return [];
    }
  }, []);

  const createChatSession = useCallback(async ({ reportId, groupId, reportName }) => {
    const response = await apiService.post(apiUrl.aipdChatSessions(), { reportId, groupId, reportName });
    return response.data?.sessionId;
  }, []);

  const getChatSessionMessages = useCallback(async (sessionId) => {
    const response = await apiService.get(apiUrl.aipdChatSessionMessages(sessionId));
    return response.data?.messages || [];
  }, []);

  /**
   * "Source Data" mode: answered from a live DAX query against the report's own
   * Power BI dataset (run under the caller's own RLS role), instead of the
   * cached visual exports the default "Tool Data" mode uses. Not streamed —
   * it's two LLM calls (write the DAX, then compose the answer from its rows)
   * bridged by a single Power BI round trip, so a plain request/response is enough.
   */
  const askSourceDataQuestion = useCallback(async ({ question, reportId, reportName, visuals }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.aipdChatReportSource(), {
        question,
        reportId,
        reportName,
        visuals,
      });
      return response.data;
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;
      setError(message);
      console.error("Error asking source-data question:", message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    askReportQuestion,
    streamReportQuestion,
    askSourceDataQuestion,
    listChatSessions,
    createChatSession,
    getChatSessionMessages,
    loading,
    error,
  };
};

